import { decodeStega, stripStega } from 'react-datocms';
import { Slider, SocialShareLinks } from '@carbonify/ui';
import clsx from 'clsx';
import BlockItem, { type Block } from 'components/Blocks';
import { AccordionEntryRecord } from 'components/Blocks/AccordionEntry';
import InlineRichText from 'components/RichContent/InlineRichText';
import dynamic from 'next/dynamic';
import { isBrowser } from 'utils/dom';

import FaqModuleContent from './FaqModuleContent';
import SlideshowModuleContent from './SlideshowModuleContent';

import styles from './GenericModule.module.css';

interface GenericModuleRecord {
  id: string;
  anchor?: string;
  __typename: string;
  title: string;
  subtitle: string;
  offset: number;
  variant: 'card_slider_section' | 'content_section' | 'accordion_section' | 'slideshow';
  theme: 'light' | 'dark' | 'highlight';
  content: Block[];
  showMedia?: boolean;
  sliderVisibleItems?: number;
}

const SocialLinks = dynamic(() => Promise.resolve(SocialShareLinks), {
  ssr: false,
});

export default function GenericModule({
  title,
  subtitle,
  offset = 0,
  variant: dirtyVariant,
  content,
  theme = 'light',
  showMedia = false,
  sliderVisibleItems = 3,
  anchor,
}: GenericModuleRecord) {
  const hasNoTitle = !title && !subtitle;
  const variant = dirtyVariant || 'content_section';

  return (
    <section
      data-testid={`generic-${variant}`}
      id={anchor}
      style={
        offset
          ? ({
              '--offset': offset,
              paddingTop: 0,
              ...(offset < 0 ? { zIndex: 1, position: 'relative' } : {}),
            } as object)
          : undefined
      }
      className={clsx(styles.module, styles[variant], styles[theme], hasNoTitle && styles.noTitle)}
    >
      {title && (
        <InlineRichText
          as="h2"
          className={styles.title}
          data-datocms-content-link-url={decodeStega(title)}
        >
          {stripStega(title)}
        </InlineRichText>
      )}
      {subtitle && (
        <InlineRichText
          as="p"
          className={styles.subtitle}
        >
          {subtitle}
        </InlineRichText>
      )}
      <div className={styles.content}>
        {variant === 'accordion_section' && <FaqModuleContent content={content as AccordionEntryRecord[]} />}
        {variant === 'card_slider_section' && (
          <Slider
            visibleItems={sliderVisibleItems}
            items={content.map(BlockItem)}
          />
        )}
        {variant === 'content_section' && content.map(BlockItem)}
        {variant === 'slideshow' && <SlideshowModuleContent content={content} />}
      </div>
      {showMedia && (
        <SocialLinks
          url={isBrowser ? window.location.href : '#'}
          title={isBrowser ? document.title : undefined}
          className={styles.socials}
        />
      )}
    </section>
  );
}
